// lib/rewardsApi.js
// R4 연속 보상 관리자 API(결정 R4-5). 계약: docs/architecture/app-v2/R6a-R4.md B3
//   GET  /v1/admin/rewards?status=pending|applied&userId=&limit=
//   POST /v1/admin/rewards/{reward_id}/apply  { "membershipId": "…" }
// api.js의 api/apiJson은 실패 응답마다 전역 에러 팝업을 띄운다. 보상 화면은 상태 코드마다
// 문구와 동작이 달라서(409는 "이미 적용됨"으로 보고 목록만 새로 고침) API 주소 규칙은 api.js와
// 같게 두고 직접 fetch한다(쿠키 동봉).
// 이 파일은 import가 없다(scripts/test-rewards.mjs가 node로 그대로 불러 시험한다).

const API_BASE =
  (typeof window !== "undefined" && window.__APP_CONFIG__?.API_BASE) ||
  process.env.VUE_APP_API_BASE || // Vue CLI
  ""; // 같은 오리진이면 빈 문자열

// 서버 limit 기본 50, 최대 200(B3)
export const REWARD_LIST_LIMIT_DEFAULT = 50;
export const REWARD_LIST_LIMIT_MAX = 200;

// status 0 = 응답을 받지 못함(네트워크 오류). serverMessage는 서버가 준 문구(있으면)
export class RewardApiError extends Error {
  constructor(status, serverMessage = "", code = "") {
    super(serverMessage || (status ? `HTTP ${status}` : "NETWORK"));
    this.name = "RewardApiError";
    this.status = status;
    this.serverMessage = serverMessage;
    this.code = code;
  }
}

// 실패 응답 본문에서 문구·코드를 꺼낸다.
// {"success":false,"error":{"code","message"}} / {"error":"…"} / {"message":"…"} / 일반 텍스트("forbidden")
async function readErrorBody(res) {
  let text = "";
  try {
    text = await res.text();
  } catch {
    return { message: "", code: "" };
  }
  try {
    const data = JSON.parse(text);
    if (data?.error && typeof data.error === "object") {
      return {
        message: String(data.error.message || ""),
        code: String(data.error.code || ""),
      };
    }
    if (typeof data?.error === "string")
      return { message: data.error, code: "" };
    if (typeof data?.message === "string") {
      return { message: data.message, code: String(data.code || "") };
    }
  } catch {
    // JSON이 아니면 아래에서 텍스트 그대로
  }
  return { message: String(text || "").trim(), code: "" };
}

async function rewardsFetch(path, opts = {}) {
  let res;
  try {
    res = await fetch(API_BASE + path, {
      credentials: "include",
      ...opts,
      headers: { "Content-Type": "application/json", ...(opts.headers || {}) },
    });
  } catch {
    throw new RewardApiError(0);
  }
  if (!res.ok) {
    const { message, code } = await readErrorBody(res);
    throw new RewardApiError(res.status, message, code);
  }
  try {
    return await res.json();
  } catch {
    throw new RewardApiError(res.status, "", "BAD_JSON");
  }
}

function clampLimit(limit) {
  const n = Math.trunc(Number(limit));
  if (!Number.isFinite(n) || n <= 0) return REWARD_LIST_LIMIT_DEFAULT;
  return Math.min(n, REWARD_LIST_LIMIT_MAX);
}

// 보상 목록. status: pending(오래된 순) | applied(최신 순). 항목 배열을 돌려준다
export async function listRewards({
  status = "pending",
  userId = "",
  limit = REWARD_LIST_LIMIT_DEFAULT,
} = {}) {
  const params = new URLSearchParams({
    status: status === "applied" ? "applied" : "pending",
    limit: String(clampLimit(limit)),
  });
  if (userId) params.set("userId", String(userId));
  const data = await rewardsFetch(`/v1/admin/rewards?${params.toString()}`);
  return Array.isArray(data?.items) ? data.items : [];
}

// 보상 적용. 성공하면 목록 항목과 같은 형태(status "applied", applied 채움)
export function applyReward(rewardId, membershipId) {
  return rewardsFetch(
    `/v1/admin/rewards/${encodeURIComponent(String(rewardId))}/apply`,
    {
      method: "POST",
      body: JSON.stringify({ membershipId }),
    }
  );
}

function isKorean(text) {
  return /[가-힣]/.test(String(text || ""));
}

// 적용 실패 → 화면 동작(kind)과 문구
//   already  409 이미 적용됨: 오류가 아니라 안내로 보이고 목록을 새로 고친다(값은 한 번만 바뀜)
//   missing  404 보상 없음: 목록을 새로 고친다(다시 받은 결과 문구는 reloadResultText가 붙인다)
//   invalid  422 수강권을 쓸 수 없음: 모달을 열어 둔 채 다른 수강권을 고르게 한다
//   forbidden 403, auth 401, network(응답 못 받음: 적용됐을 수 있어 목록을 새로 고친다), error
export function describeApplyError(err) {
  const status = err?.status;
  switch (status) {
    case 409:
      return {
        kind: "already",
        message:
          "이미 적용된 보상이에요. 다른 관리자가 먼저 적용했거나 한 번 더 눌렸어요. 수강권 값은 한 번만 바뀌었어요.",
      };
    case 404:
      return {
        kind: "missing",
        message: "보상을 찾을 수 없어요.",
      };
    case 422:
      return {
        kind: "invalid",
        message: isKorean(err?.serverMessage)
          ? err.serverMessage
          : "이 수강권에는 적용할 수 없어요. 이 회원이 지금 사용 중인 수강권을 골라 주세요.",
      };
    case 403:
      return {
        kind: "forbidden",
        message: "관리자 계정만 보상을 적용할 수 있어요.",
      };
    case 401:
      return {
        kind: "auth",
        message: "로그인이 만료됐어요. 다시 로그인한 뒤 적용해 주세요.",
      };
    case 0:
      return {
        kind: "network",
        message:
          "서버 응답을 받지 못했어요. 적용됐을 수 있으니 목록을 확인한 뒤 다시 시도해 주세요.",
      };
    default:
      return {
        kind: "error",
        message: `적용하지 못했어요 (오류 ${status}). 잠시 뒤 다시 시도해 주세요.`,
      };
  }
}

// 적용 실패(404·422) 뒤 대기 목록을 다시 받은 결과 문구. reloaded: 다시 받기가 성공해 화면 목록이 새 값인지.
// 다시 받기가 실패하면 화면에는 예전 목록(방금 서버가 거절한 수강권 포함)이 그대로라 "새로 불러왔어요"라고 하지 않는다
export function reloadResultText(kind, reloaded) {
  if (kind === "missing") {
    return reloaded
      ? "목록을 새로 불러왔어요."
      : "목록을 새로 불러오지 못했어요. 아래 [다시 불러오기]를 눌러 주세요.";
  }
  return reloaded
    ? "수강권 목록을 새로 불러왔어요. 확인한 뒤 다시 적용해 주세요."
    : "수강권 목록을 새로 불러오지 못했어요. [목록 다시 불러오기]를 누른 뒤 다시 적용해 주세요.";
}

// 목록 조회 실패 문구
export function describeListError(err) {
  const status = err?.status;
  if (status === 401) return "로그인이 만료됐어요. 다시 로그인해 주세요.";
  if (status === 403) return "관리자 계정만 연속 보상을 볼 수 있어요.";
  if (status === 0) {
    return "서버에 연결하지 못했어요. 잠시 뒤 다시 불러와 주세요.";
  }
  return `연속 보상 목록을 불러오지 못했어요 (오류 ${status}).`;
}
